import { Link, useRouterState } from "@tanstack/react-router";
import { PanelLeftCloseIcon, PanelLeftOpenIcon, ShapesIcon } from "lucide-react";
import { DatanomyLogo } from "@/components/brand";
import { Button } from "@/components/ui/button";
import { ScrollArea, SimpleTooltip } from "@/components/ui/primitives";
import { cn } from "@/lib/utils";
import type { Workspace } from "./nav";

interface SidebarProps {
  workspaces: Workspace[];
  collapsed: boolean;
  onToggleCollapsed?: () => void;
  /** Called after navigating (used to close the mobile sheet). */
  onNavigate?: () => void;
  className?: string;
}

const itemBase =
  "group flex h-8 items-center gap-2.5 rounded-md px-2 text-[13px] font-medium text-sidebar-foreground transition-colors outline-none hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 focus-visible:ring-sidebar-ring/50";

export function Sidebar({ workspaces, collapsed, onToggleCollapsed, onNavigate, className }: SidebarProps) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const activeId = pathname === "/" ? "home" : pathname.split("/")[1];

  return (
    <div
      className={cn(
        "flex h-full flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground",
        collapsed ? "w-[60px]" : "w-60",
        "transition-[width] duration-200 ease-out",
        className,
      )}
    >
      <div className={cn("flex h-14 shrink-0 items-center gap-2 px-3.5", collapsed && "justify-center px-0")}>
        <Link to="/" onClick={onNavigate} className="flex items-center gap-2 rounded-md outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring/50" aria-label="Pulse home">
          {collapsed ? (
            <DatanomyLogo variant="mark" />
          ) : (
            <>
              <DatanomyLogo className="h-6" />
              <span aria-hidden="true" className="h-4 w-px bg-sidebar-border" />
              <span className="text-[13px] font-semibold tracking-tight text-sidebar-accent-foreground">Pulse</span>
            </>
          )}
        </Link>
      </div>

      <ScrollArea className="flex-1">
        <nav aria-label="Workspaces" className={cn("flex flex-col gap-0.5 px-2 pb-4", collapsed && "items-center")}>
          {workspaces.map((ws) => {
            const active = ws.id === activeId;
            const Icon = ws.icon;
            const firstSection = ws.sections[0];
            const linkProps =
              ws.id === "home" || !firstSection
                ? ({ to: "/" } as const)
                : ({ to: "/$workspace/$section", params: { workspace: ws.id, section: firstSection.slug } } as const);

            const link = (
              <Link
                {...linkProps}
                onClick={onNavigate}
                aria-current={active ? "page" : undefined}
                className={cn(
                  itemBase,
                  collapsed && "size-9 justify-center px-0",
                  active && "bg-sidebar-accent text-sidebar-accent-foreground",
                )}
              >
                <Icon
                  className={cn(
                    "size-4 shrink-0 text-sidebar-muted-foreground group-hover:text-sidebar-accent-foreground",
                    active && "text-sidebar-primary group-hover:text-sidebar-primary",
                  )}
                  aria-hidden="true"
                />
                {!collapsed && <span className="truncate">{ws.label}</span>}
              </Link>
            );

            return (
              <div key={ws.id} className={cn(!collapsed && "flex flex-col")}>
                {collapsed ? (
                  <SimpleTooltip label={ws.label} side="right">
                    {link}
                  </SimpleTooltip>
                ) : (
                  link
                )}
                {!collapsed && active && ws.sections.length > 0 && (
                  <ul className="my-1 ml-[17px] flex flex-col gap-px border-l border-sidebar-border pl-2.5">
                    {ws.sections.map((s) => (
                      <li key={s.slug}>
                        <Link
                          to="/$workspace/$section"
                          params={{ workspace: ws.id, section: s.slug }}
                          onClick={onNavigate}
                          className="flex h-7 items-center rounded-md px-2 text-[13px] text-sidebar-muted-foreground transition-colors outline-none hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 focus-visible:ring-sidebar-ring/50"
                          activeProps={{
                            className: "!text-sidebar-accent-foreground font-medium bg-sidebar-accent/70",
                            "aria-current": "page",
                          }}
                        >
                          <span className="truncate">{s.label}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </nav>
      </ScrollArea>

      <div className={cn("flex shrink-0 flex-col gap-0.5 border-t border-sidebar-border p-2", collapsed && "items-center")}>
        {import.meta.env.DEV && (
          <SimpleTooltip label="Component gallery (dev only)" side="right">
            <Link to="/dev/components" onClick={onNavigate} className={cn(itemBase, collapsed && "size-9 justify-center px-0")}>
              <ShapesIcon className="size-4 text-sidebar-muted-foreground" aria-hidden="true" />
              {!collapsed && <span>Components</span>}
            </Link>
          </SimpleTooltip>
        )}
        {onToggleCollapsed && (
          <Button
            variant="ghost"
            size={collapsed ? "icon" : "sm"}
            onClick={onToggleCollapsed}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            className={cn(
              "text-sidebar-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
              !collapsed && "justify-start px-2",
            )}
          >
            {collapsed ? <PanelLeftOpenIcon /> : <PanelLeftCloseIcon />}
            {!collapsed && <span className="text-[13px]">Collapse</span>}
          </Button>
        )}
      </div>
    </div>
  );
}
